import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import Screen from '../../components/common/Screen';
import Title from '../../components/common/Title';
import Back from '../../components/common/Back';
import Icon from '../../components/common/Icon';
import Card from '../../components/elements/Card';
import Button from '../../components/elements/Button';
import { PurposePicker, PURPOSE_KINDS } from '../../components/common/EventTags';
import { apiErrorText, apiRequest } from '../../utils';
import './MemberHome.css';
import './SendCheer.css';

/* Cheering a colleague — the composer. F7.

   ★ Three numbered steps, and the ORDER is the owner's call (2026-09-09): a person, then what
   they actually DID, then which of the things we stand for it was. The sentence is the cheer —
   the chips are a label on it — so the screen asks for the substance first and files it under a
   value second. (The build opened with the chips, on the argument that naming a value first
   turns the last box into a question with an answer. That risk is real and the copy now carries
   it instead: the hint showing what a concrete sentence looks like sits ABOVE the box, where it
   is read before writing rather than after.)

   ★ Step 3 is OPTIONAL and says so. Only the person and the words are required — the store has
   always accepted a cheer with no chips — but a pale button under three unanswered headings made
   all three of them look mandatory.

   ★ Nobody is a "they" here. Once a person is picked, every question and every promise on the
   screen uses their NAME: "What did Arjun do?", "Only Arjun sees this." One colleague is one
   person, and a plural pronoun made the screen read as if it were about a department.

   ★ The chips are the company's OWN, straight out of Programmes (`company_purpose_catalog`), and
   a company that has declared nothing simply does not get step 3. Never an empty box: three
   headings with nothing under them is three questions nobody here can answer yet.

   ★ Up to three, on the owner's call — one act really can serve a value AND a goal AND the
   quarter's theme. The cap is enforced in the store too; here it is a picker that stops
   responding, which is the version somebody can see coming.

   ★ The line under the button says what is still MISSING while anything is, and falls back to the
   daily count only once the button is live. A disabled primary button with no reason beside it is
   the commonest way a form gets abandoned.

   ★ Nothing is public. The lock line above the button says so, because a person writing about a
   colleague deserves to know who will read it BEFORE they write, not after. */

/* The name people are called by. "What did Arjun Deshpande do?" is a form; "What did Arjun do?"
   is a question a colleague asks. */
const firstName = (name) => String(name || '').trim().split(/\s+/)[0] || '';

const CheerPerson = ({ person, on, onClick }) => (
  <button type="button" className={`sc-person ${on ? 'is-on' : ''}`} onClick={onClick}>
    {person.photo_url
      ? <img src={person.photo_url} alt="" className="sc-person__img" />
      : <span className="sc-person__img sc-person__img--blank"><Icon name="user" /></span>}
    <span className="sc-person__name">{person.name}</span>
  </button>
);

/* One step: a number that becomes a tick once the step is answered, the question itself, and the
   one line saying how to answer it. The tick is the point — it is what tells somebody halfway
   down the screen how much of this is left. */
const Step = ({ n, done, title, note, optional = false, children }) => {
  const { t } = useTranslation();
  return (
    <Card>
      <div className="sc-step">
        <span className={`sc-step__n ${done ? 'is-done' : ''}`}>
          {done ? <Icon name="check" /> : n}
        </span>
        <div className="sc-step__head">
          <p className="sc-step__title">
            {title}
            {optional && <span className="sc-step__opt">{t('optional')}</span>}
          </p>
          {note && <p className="sc-step__note">{note}</p>}
        </div>
      </div>
      {children}
    </Card>
  );
};

const SendCheer = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const params = useParams();
  const [search] = useSearchParams();

  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [to, setTo] = useState(params.partnerId || search.get('to') || '');
  const [picks, setPicks] = useState({ theme: [], value: [], sdg: [] });
  const [body, setBody] = useState('');
  const [query, setQuery] = useState('');
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  useEffect(() => {
    let alive = true;
    apiRequest('GET', '/recognition/catalog')
      .then((res) => { if (alive) setData(res); })
      .catch((e) => { if (alive) setError(apiErrorText(t, e, 'somethingWentWrong')); });
    return () => { alive = false; };
  }, [t]);

  const chosen = (data?.people || []).find((p) => p.id === to) || null;
  const who = firstName(chosen?.name);
  const max = data?.max_purposes || 3;
  const bodyMax = data?.body_max || 500;
  const picked = PURPOSE_KINDS.reduce((n, kind) => n + (picks[kind] || []).length, 0);
  const written = body.trim().length > 0;

  /* Toggling off always works; toggling ON stops at the cap. A picker that silently drops the
     fourth pick teaches people the screen is broken. */
  const toggle = useCallback((kind, id) => {
    setPicks((prev) => {
      const on = prev[kind] || [];
      const has = on.includes(id);
      if (!has) {
        const total = PURPOSE_KINDS.reduce((n, k) => n + (prev[k] || []).length, 0);
        if (total >= max) return prev;
      }
      return { ...prev, [kind]: has ? on.filter((x) => x !== id) : [...on, id] };
    });
  }, [max]);

  const submit = async () => {
    setSending(true);
    setError(null);
    try {
      await apiRequest('POST', '/recognition', {
        to,
        body,
        purposes: PURPOSE_KINDS.flatMap((kind) =>
          (picks[kind] || []).map((id) => ({ kind, id }))),
      });
      setSent(true);
    } catch (e) {
      setError(apiErrorText(t, e, 'somethingWentWrong'));
    } finally {
      setSending(false);
    }
  };

  if (sent) {
    return (
      <Screen>
        <Back to="/member-home" />
        <div className="top">
          <Card>
            <div className="sc-done">
              <Icon name="hands-clapping" theme="primary" className="sc-done__icon" />
              <Title my="1">{t('cheerSentTitle')}</Title>
              <p className="ce-note">{t('cheerSentNote', { name: chosen?.name || '' })}</p>
              <Button theme="primary" onClick={() => navigate('/member-home')}>
                {t('done')}
              </Button>
            </div>
          </Card>
        </div>
      </Screen>
    );
  }

  const people = (data?.people || []).filter(
    (p) => !query || p.name.toLowerCase().includes(query.trim().toLowerCase()));
  const noneLeft = data && data.left_today <= 0;

  /* What is still in the way, in the order the steps ask for it. Empty once the button works. */
  const missing = !to
    ? t('cheerMissingWho')
    : (!written ? t('cheerMissingWords', { name: who }) : '');

  return (
    <Screen>
      <Back to="/member-home" />
      <div className="top">
        <Title>{t('cheerTitle')}</Title>
        <p className="ce-note mx-3">{t('cheerIntro')}</p>

        {error && <p className="bravos-cal__notice mx-3">{error}</p>}
        {!data && !error && <span className="auth__spinner" />}

        {data && noneLeft && (
          <Card><p className="ce-note">{t('cheerDailyLimit')}</p></Card>
        )}

        {data && !noneLeft && (
          <>
            {/* 1 — who */}
            <Step n={1} done={!!chosen} title={t('cheerStepWho')} note={t('cheerStepWhoNote')}>
              {chosen ? (
                <div className="sc-chosen">
                  <CheerPerson person={chosen} on onClick={() => setTo('')} />
                  <button type="button" className="sc-change" onClick={() => setTo('')}>
                    {t('cheerChangePerson')}
                  </button>
                </div>
              ) : (
                <>
                  <input className="gl-search" type="text" value={query}
                         placeholder={t('communitySearch')}
                         onChange={(e) => setQuery(e.target.value)} />
                  <div className="sc-people">
                    {people.map((p) => (
                      <CheerPerson key={p.id} person={p} on={false}
                                   onClick={() => { setTo(p.id); setQuery(''); }} />
                    ))}
                  </div>
                  {people.length === 0 && <p className="ce-note">{t('communityNoMatch')}</p>}
                </>
              )}
            </Step>

            {/* 2 — what the person did. The cheer itself, so it comes before the label on it. */}
            <Step n={2} done={written} note={t('cheerWordsHint')}
                  title={chosen
                    ? t('cheerStepWordsNamed', { name: who })
                    : t('cheerStepWords')}>
              <textarea className="sc-body" rows={4} value={body}
                        maxLength={bodyMax}
                        placeholder={t('cheerWordsPlaceholder')}
                        onChange={(e) => setBody(e.target.value)} />
              <p className="sc-count">{body.length}/{bodyMax}</p>
            </Step>

            {/* 3 — what it stood for. Absent, not empty, when the company declared nothing. */}
            <Step n={3} done={picked > 0} optional title={t('cheerStepWhat')}
                  note={t('cheerPickUpTo', { n: max })}>
              <PurposePicker catalog={data.catalog} picks={picks} onToggle={toggle}
                             note={t('cheerChipsNote')} />
              {picked >= max && <p className="ce-note">{t('cheerChipsFull', { n: max })}</p>}
            </Step>
          </>
        )}
      </div>

      {data && !noneLeft && (
        <div className="bottom">
          <div className="px-3">
            <p className="ce-note sc-privacy">
              <Icon name="lock" className="me-2" />
              {chosen ? t('cheerPrivacyNoteNamed', { name: who }) : t('cheerPrivacyNote')}
            </p>
            <Button theme="primary" disabled={!to || !written || sending} onClick={submit}>
              {sending ? t('sending') : t('cheerSend')}
            </Button>
            <p className={`ce-note text-center ${missing ? 'sc-missing' : ''}`}>
              {missing || t('cheerLeftToday', { n: data.left_today })}
            </p>
          </div>
        </div>
      )}
    </Screen>
  );
};

export default SendCheer;
